import React, { useContext, useEffect } from 'react';
import {
  HashRouter,
  Switch,
  Route,
  withRouter,
  Redirect,
} from 'react-router-dom';

import { useSelector } from 'react-redux';
import { dataSelector } from '@nextiva/global-store';
import {
  routeWithPermissions,
  IsRoutePermitted,
  CREATE_PLATFORM_ROLE,
  UPDATE_PLATFORM_ROLE,
  CREATE_PLATFORM_DEPARTMENT,
  UPDATE_PLATFORM_DEPARTMENT,
} from 'containers';
import {
  ACCOUNT_INFO,
  LICENSING,
  USERS,
  THANK_YOU,
  TEAMS,
  TEAMS_CALL_GROUP,
  TEAMS_MESSAGING,
  TEAMS_SUCCESS,
  ROLES,
  MESSAGING,
  CONTACT_CENTER,
  RESET_DATA_STATUS,
  ADVANCED_ROLES,
  ADVANCED_ROLES_CREATE,
  ADVANCED_ROLES_CREATE_PERMISSIONS,
  ADVANCED_ROLES_ROLE_ID,
  ADVANCED_ROLES_ROLE_ID_PERMISSIONS,
  EMAIL,
  DATA_IMPORT,
  SCHEDULES,
} from 'constants/routes';
import { Header } from 'components';
import { FeatureContext } from 'components/AccessResolvers/featureContext';

import { AdminHome } from 'containers/pages/AdminHome/AdminHome';
import { App } from './containers/App';
import {
  AccountInfo,
  BillingLicenses,
  NotFound,
  ManageRole,
  AdvancedRoles,
  Roles,
  MessagingNew,
  ContactCenter,
  ManageUsers,
  RoleProducts,
  ModelsPermissions,
  FieldsPermissions,
  ResetDataStatus,
  ResetData,
  Email,
  EmailSetup,
  TeamCallGroup,
  Teams,
  TeamsMessaging,
  ThankYou,
  DataImport,
  Schedules,
} from './containers/pages';

const ManageRoleWithPermissions = withRouter(({ match, children }) => {
  const permission =
    match.params.actionType === 'edit'
      ? UPDATE_PLATFORM_ROLE
      : CREATE_PLATFORM_ROLE;

  return (
    <IsRoutePermitted permissions={[permission]}>
      <ManageRole>{children}</ManageRole>
    </IsRoutePermitted>
  );
});

const AccountInformationWithPermissions = routeWithPermissions(AccountInfo);

const BillingLicensesWithPermissions = routeWithPermissions(BillingLicenses);

const ManageUsersWithPermissions = routeWithPermissions(ManageUsers);

const RoleWithPermissions = routeWithPermissions(Roles);

const TeamsWithPermissions = withRouter(({ match, children }) => {
  const permission =
    match.params.actionType === 'edit'
      ? UPDATE_PLATFORM_DEPARTMENT
      : CREATE_PLATFORM_DEPARTMENT;

  return (
    <IsRoutePermitted permissions={[permission]}>
      <Teams>{children}</Teams>
    </IsRoutePermitted>
  );
});

const ResetDataStatusWithPermissions = ({ children }) => {
  return (
    <React.Fragment>
      {window.NextivaEnv === 'demo' && (
        <ResetDataStatus>{children}</ResetDataStatus>
      )}
    </React.Fragment>
  );
};

const Routes = () => {
  const globalStoreData = useSelector(dataSelector);
  const {
    featureState: { authV3Enabled },
  } = useContext(FeatureContext);

  useEffect(() => {
    if (!globalStoreData?.user) return;

    if (!window.DD_RUM) {
      // eslint-disable-next-line no-console
      console.error('DD RUM failed to load, probably due to an ad blocker.');

      return;
    }

    // adds logged in user's info to the DataDog context, which can be used for tracking via DD RUM + Kibana
    if (typeof window.DD_RUM?.setUser === 'function') {
      window.DD_RUM.setUser({
        useruuid: globalStoreData?.user?.nextivaUUID,
        email: globalStoreData?.user?.email,
        corpAcctNumber: globalStoreData?.user?.accountInfo?.corpAcctNbr,
      });
    }
  }, [globalStoreData?.user, window.DD_RUM?.setUser]);

  return (
    <HashRouter>
      <App>
        <Header>
          <Switch>
            <Route path="/home">
              <AdminHome />
            </Route>
            <Route exact path={['/', ACCOUNT_INFO]}>
              {authV3Enabled ? (
                <AccountInfo />
              ) : (
                <AccountInformationWithPermissions />
              )}
            </Route>

            <Route path={SCHEDULES}>
              <Schedules />
            </Route>

            <Route path={`${LICENSING}/:tab/:license`}>
              {authV3Enabled ? (
                <BillingLicenses />
              ) : (
                <BillingLicensesWithPermissions />
              )}
            </Route>

            <Route path={`${LICENSING}/:tab`}>
              {authV3Enabled ? (
                <BillingLicenses />
              ) : (
                <BillingLicensesWithPermissions />
              )}
            </Route>

            <Route path={LICENSING}>
              {authV3Enabled ? (
                <BillingLicenses />
              ) : (
                <BillingLicensesWithPermissions />
              )}
            </Route>

            <Route exact path={USERS}>
              {authV3Enabled ? <ManageUsers /> : <ManageUsersWithPermissions />}
            </Route>

            <Route path={THANK_YOU}>
              <ThankYou pageTitle="Users/Create User" />
            </Route>
            <Route path={`${TEAMS_CALL_GROUP}/:groupId/:team`}>
              <TeamCallGroup />
            </Route>

            <Route path={TEAMS_MESSAGING}>
              <TeamsMessaging />
            </Route>

            <Route path={TEAMS_SUCCESS}>
              <ThankYou pageTitle="Teams/Create Team" />
            </Route>

            <Route path={TEAMS}>
              {authV3Enabled ? <Teams /> : <TeamsWithPermissions />}
            </Route>

            <Route path={ROLES}>
              {authV3Enabled ? <Roles /> : <RoleWithPermissions />}
            </Route>

            <Route path={MESSAGING}>
              <MessagingNew />
            </Route>

            <Route path={CONTACT_CENTER}>
              <ContactCenter />
            </Route>

            <Route exact path={DATA_IMPORT}>
              <DataImport />
            </Route>

            <Route exact path={`${DATA_IMPORT}/phones-and-devices`}>
              <Redirect
                to={{
                  pathname: `/data-import`,
                  state: 'DEVICE',
                }}
              />
            </Route>

            <Route exact path={RESET_DATA_STATUS}>
              <ResetDataStatusWithPermissions />
            </Route>

            <Route exact path={`${RESET_DATA_STATUS}/reset`}>
              <ResetDataStatusWithPermissions>
                <ResetData />
              </ResetDataStatusWithPermissions>
            </Route>

            <Route exact path={ADVANCED_ROLES}>
              <AdvancedRoles />
            </Route>

            <Route exact path={ADVANCED_ROLES_CREATE}>
              <ManageRole>
                <RoleProducts />
              </ManageRole>
            </Route>

            <Route exact path={ADVANCED_ROLES_CREATE_PERMISSIONS}>
              <ManageRoleWithPermissions>
                <ModelsPermissions />
              </ManageRoleWithPermissions>
            </Route>

            <Route exact path={ADVANCED_ROLES_ROLE_ID}>
              <ManageRoleWithPermissions>
                <RoleProducts />
              </ManageRoleWithPermissions>
            </Route>

            <Route exact path={ADVANCED_ROLES_ROLE_ID_PERMISSIONS}>
              <ManageRoleWithPermissions>
                <ModelsPermissions />
              </ManageRoleWithPermissions>
            </Route>

            <Route
              exact
              path={`${ADVANCED_ROLES_ROLE_ID_PERMISSIONS}/:modelName`}
            >
              <ManageRoleWithPermissions>
                <FieldsPermissions />
              </ManageRoleWithPermissions>
            </Route>

            <Route exact path={EMAIL}>
              <Email />
            </Route>

            <Route exact path={`${EMAIL}/add`}>
              <EmailSetup />
            </Route>

            <Route exact path={`${EMAIL}/:email`}>
              <Email />
            </Route>

            <Route path="/*">
              <NotFound />
            </Route>
          </Switch>
        </Header>
      </App>
    </HashRouter>
  );
};

export default Routes;
